/**
 * @module dialogs
 * Modal dialog system for MyPad++.
 */

import { LANGUAGE_LIST } from '../editor/languages.js';
import { ICONS } from './toolbar.js';
import { t } from '../i18n.js';

// ─── Encoding data ───────────────────────────────────────────────────────────

const ENCODING_GROUPS = {
  Unicode: ['UTF-8', 'UTF-16 LE', 'UTF-16 BE', 'UTF-32 LE', 'UTF-32 BE'],
  Chinese: ['GB2312', 'GBK', 'GB18030', 'Big5', 'EUC-TW', 'HZ'],
  Japanese: ['Shift_JIS', 'EUC-JP', 'ISO-2022-JP', 'CP932'],
  Korean: ['EUC-KR', 'ISO-2022-KR', 'CP949'],
  Western: [
    'Windows-1252', 'ISO-8859-1', 'ISO-8859-15', 'Macintosh',
    'Windows-1250', 'ISO-8859-2',
    'Windows-1251', 'ISO-8859-5', 'KOI8-R', 'KOI8-U',
    'Windows-1253', 'ISO-8859-7',
    'Windows-1254', 'ISO-8859-9',
    'Windows-1255', 'ISO-8859-8',
    'Windows-1256', 'ISO-8859-6',
    'Windows-1257', 'ISO-8859-13',
    'Windows-1258',
    'ISO-8859-3', 'ISO-8859-4', 'ISO-8859-10', 'ISO-8859-14', 'ISO-8859-16',
    'TIS-620',
  ],
};

// ─── Dialog infrastructure ───────────────────────────────────────────────────

/**
 * Create the dialog backdrop + container, wire up Escape, focus trap, and animations.
 * @param {object} opts
 * @param {string} opts.className - Additional CSS class for the dialog box.
 * @param {string} [opts.ariaLabel]
 * @param {Function} [opts.onClose] - Called when dialog is dismissed.
 * @returns {{ overlay: HTMLElement, dialog: HTMLElement, close: () => void }}
 */
function createDialogBase({ className, ariaLabel, onClose }) {
  const overlay = document.createElement('div');
  overlay.className = 'dialog-overlay';

  const dialog = document.createElement('div');
  dialog.className = `dialog ${className}`;
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  if (ariaLabel) dialog.setAttribute('aria-label', ariaLabel);
  dialog.tabIndex = -1;

  dialog.addEventListener('focusin', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      if (localStorage.getItem('mypad_keyboard_enabled') === 'false') {
        e.target.setAttribute('inputmode', 'none');
      } else {
        e.target.removeAttribute('inputmode');
      }
    }
  });

  overlay.appendChild(dialog);

  function close() {
    overlay.classList.add('dialog-overlay--closing');
    dialog.classList.add('dialog--closing');
    const onEnd = () => {
      overlay.remove();
      overlay.removeEventListener('animationend', onEnd);
    };
    overlay.addEventListener('animationend', onEnd);
    // Fallback
    setTimeout(() => { if (overlay.parentNode) overlay.remove(); }, 300);
    document.removeEventListener('keydown', onKeyDown);
    if (typeof onClose === 'function') onClose();
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
    // Focus trap
    if (e.key === 'Tab') {
      const focusable = dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // Close on backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  document.addEventListener('keydown', onKeyDown);
  document.body.appendChild(overlay);

  // Trigger the appear animation on next frame
  requestAnimationFrame(() => {
    overlay.classList.add('visible');

    const firstFocusable = dialog.querySelector(
      'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus();
    } else {
      dialog.focus();
    }
  });

  return { overlay, dialog, close };
}

/**
 * Create a dialog header with title and close button.
 * @param {string} titleText
 * @param {Function} onClose
 * @returns {HTMLElement}
 */
function createDialogHeader(titleText, onClose) {
  const header = document.createElement('div');
  header.className = 'dialog-header';

  const title = document.createElement('h2');
  title.className = 'dialog-title';
  title.textContent = titleText;

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'dialog-close-btn';
  closeBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>`;
  closeBtn.title = t('Close');
  closeBtn.setAttribute('aria-label', t('Close dialog'));
  closeBtn.addEventListener('click', onClose);

  header.appendChild(title);
  header.appendChild(closeBtn);
  return header;
}

/**
 * Create a standard footer with action buttons.
 * @param {Array<{label: string, className?: string, onClick: Function}>} buttons
 * @returns {HTMLElement}
 */
function createDialogFooter(buttons) {
  const footer = document.createElement('div');
  footer.className = 'dialog-footer';

  buttons.forEach(({ label, className, onClick }) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `dialog-btn ${className || ''}`.trim();
    btn.textContent = label;
    btn.addEventListener('click', onClick);
    footer.appendChild(btn);
  });

  return footer;
}

// ─── Dialog exports ──────────────────────────────────────────────────────────

/**
 * Show a dialog to select an open tab for comparison.
 *
 * @param {Array<{id: string, filename: string}>} tabs - List of open tabs.
 * @param {(selectedId: string) => void} onSelect - Callback when a tab is selected.
 */
export function showCompareSelectorDialog(tabs, onSelect) {
  const { dialog, close } = createDialogBase({
    className: 'dialog-encoding-picker', // Reuse grid styling
    ariaLabel: t('Compare with...'),
    onClose: () => {},
  });

  dialog.appendChild(createDialogHeader(t('Compare with...'), close));

  const body = document.createElement('div');
  body.className = 'dialog-body dialog-encoding-body';

  const section = document.createElement('div');
  section.className = 'dialog-encoding-section';

  const grid = document.createElement('div');
  grid.className = 'dialog-encoding-grid';

  if (tabs.length === 0) {
    const emptyMsg = document.createElement('div');
    emptyMsg.textContent = t('No other tabs open to compare with.');
    emptyMsg.style.padding = 'var(--space-4)';
    emptyMsg.style.color = 'var(--text-secondary)';
    section.appendChild(emptyMsg);
  } else {
    tabs.forEach((tab) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dialog-encoding-item';
      btn.textContent = tab.webdavPath || tab.filePath || tab.filename || t('Untitled');
      btn.title = btn.textContent;
      btn.style.whiteSpace = 'nowrap';
      btn.style.overflow = 'hidden';
      btn.style.textOverflow = 'ellipsis';
      btn.addEventListener('click', () => {
        if (typeof onSelect === 'function') onSelect(tab.id);
        close();
      });
      grid.appendChild(btn);
    });
    section.appendChild(grid);
  }

  body.appendChild(section);
  dialog.appendChild(body);

  return { close };
}

/**
 * Show a language picker dialog.
 *
 * @param {string} currentLanguage - The currently active language name.
 * @param {(languageName: string) => void} onSelect - Called with the chosen language name.
 */
export function showLanguagePicker(currentLanguage, onSelect) {
  const { dialog, close } = createDialogBase({
    className: 'dialog-encoding-picker', // Reuse encoding picker styles
    ariaLabel: t('Select Language Mode'),
    onClose: () => {},
  });

  dialog.appendChild(createDialogHeader(t('Language Mode'), close));

  // Search box
  const searchWrap = document.createElement('div');
  searchWrap.className = 'dialog-search-wrap';

  const searchInput = document.createElement('input');
  searchInput.type = 'text';
  searchInput.className = 'dialog-search-input';
  searchInput.placeholder = t('Search languages…');
  searchInput.setAttribute('aria-label', t('Search languages'));
  searchWrap.appendChild(searchInput);
  dialog.appendChild(searchWrap);

  // Grid body
  const body = document.createElement('div');
  body.className = 'dialog-body dialog-encoding-body';

  const section = document.createElement('div');
  section.className = 'dialog-encoding-section';

  const grid = document.createElement('div');
  grid.className = 'dialog-encoding-grid';

  // Add Plain Text first
  const languages = [{ name: 'Plain Text' }, ...LANGUAGE_LIST];

  languages.forEach((lang) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dialog-encoding-item';
    if (lang.name.toUpperCase() === (currentLanguage || '').toUpperCase()) {
      btn.classList.add('dialog-encoding-item--active');
    }
    btn.textContent = lang.name;
    btn.setAttribute('aria-label', lang.name);
    btn.addEventListener('click', () => {
      if (typeof onSelect === 'function') onSelect(lang.name);
      close();
    });
    grid.appendChild(btn);
  });

  section.appendChild(grid);
  body.appendChild(section);
  dialog.appendChild(body);

  // Search filter
  searchInput.addEventListener('input', () => {
    const query = searchInput.value.toLowerCase().trim();
    const items = grid.querySelectorAll('.dialog-encoding-item');
    items.forEach((btn) => {
      const match = btn.textContent.toLowerCase().includes(query);
      btn.style.display = match ? '' : 'none';
    });
  });

  return { close };
}

/**
 * Show an encoding picker dialog.
 *
 * @param {string} currentEncoding - The currently active encoding.
 * @param {(encoding: string) => void} onSelect - Called with the chosen encoding.
 */
export function showEncodingPicker(currentEncoding, onSelect) {
  const { dialog, close } = createDialogBase({
    className: 'dialog-encoding-picker',
    ariaLabel: t('Select File Encoding'),
    onClose: () => {},
  });

  dialog.appendChild(createDialogHeader(t('File Encoding'), close));

  // Search box
  const searchWrap = document.createElement('div');
  searchWrap.className = 'dialog-search-wrap';

  const searchInput = document.createElement('input');
  searchInput.type = 'text';
  searchInput.className = 'dialog-search-input';
  searchInput.placeholder = t('Search encodings...');
  searchInput.setAttribute('aria-label', t('Search encodings...'));
  searchWrap.appendChild(searchInput);
  dialog.appendChild(searchWrap);

  // Grid body
  const body = document.createElement('div');
  body.className = 'dialog-body dialog-encoding-body';

  /** @type {Map<string, HTMLElement>} */
  const itemElements = new Map();

  Object.entries(ENCODING_GROUPS).forEach(([category, encodings]) => {
    const section = document.createElement('div');
    section.className = 'dialog-encoding-section';
    section.dataset.category = category;

    const catTitle = document.createElement('h3');
    catTitle.className = 'dialog-encoding-category';
    catTitle.textContent = category;
    section.appendChild(catTitle);

    const grid = document.createElement('div');
    grid.className = 'dialog-encoding-grid';

    encodings.forEach((enc) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dialog-encoding-item';
      if (enc.toUpperCase() === (currentEncoding || '').toUpperCase()) {
        btn.classList.add('dialog-encoding-item--active');
      }
      btn.textContent = enc;
      btn.setAttribute('aria-label', enc);
      btn.addEventListener('click', () => {
        if (typeof onSelect === 'function') onSelect(enc);
        close();
      });
      grid.appendChild(btn);
      itemElements.set(enc.toLowerCase(), btn);
    });

    section.appendChild(grid);
    body.appendChild(section);
  });

  dialog.appendChild(body);

  // Search filter
  searchInput.addEventListener('input', () => {
    const query = searchInput.value.toLowerCase().trim();
    body.querySelectorAll('.dialog-encoding-section').forEach((section) => {
      const items = section.querySelectorAll('.dialog-encoding-item');
      let anyVisible = false;
      items.forEach((btn) => {
        const match = btn.textContent.toLowerCase().includes(query);
        btn.style.display = match ? '' : 'none';
        if (match) anyVisible = true;
      });
      section.style.display = anyVisible ? '' : 'none';
    });
  });
}

/**
 * Show a Go To Line dialog.
 *
 * @param {number} maxLine - Maximum valid line number.
 * @param {(line: number) => void} onGo - Called with the target line number.
 */
export function showGoToLineDialog(maxLine, onGo) {
  const { dialog, close } = createDialogBase({
    className: 'dialog-goto-line',
    ariaLabel: t('Go to Line'),
    onClose: () => {},
  });

  dialog.appendChild(createDialogHeader(t('Go to Line'), close));

  const body = document.createElement('div');
  body.className = 'dialog-body';

  const label = document.createElement('label');
  label.className = 'dialog-label';
  label.textContent = t('Line number (1 - {max}):', { max: maxLine });

  const input = document.createElement('input');
  input.type = 'number';
  input.className = 'dialog-input';
  input.min = '1';
  input.max = String(maxLine);
  input.placeholder = 'Line number';
  input.setAttribute('aria-label', 'Line number');
  label.htmlFor = 'goto-line-input';
  input.id = 'goto-line-input';

  body.appendChild(label);
  body.appendChild(input);
  dialog.appendChild(body);

  function handleGo() {
    const val = parseInt(input.value, 10);
    if (isNaN(val) || val < 1 || val > maxLine) {
      input.classList.add('dialog-input--error');
      input.focus();
      return;
    }
    if (typeof onGo === 'function') onGo(val);
    close();
  }

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGo();
    }
  });

  input.addEventListener('input', () => {
    input.classList.remove('dialog-input--error');
  });

  dialog.appendChild(createDialogFooter([
    { label: t('Cancel'), className: 'dialog-btn--secondary', onClick: close },
    { label: t('Go'), className: 'dialog-btn--primary', onClick: handleGo },
  ]));
}

/**
 * Show a general Confirmation dialog.
 * @param {string} title - The title of the dialog
 * @param {string} message - The message to display
 * @param {Function} onConfirm - Callback on Confirm
 * @param {Function} onCancel - Callback on Cancel
 */
export function showConfirmDialog(title, message, onConfirm, onCancel) {
  const { dialog, close } = createDialogBase({
    className: 'dialog-confirm',
    ariaLabel: title,
    onClose: onCancel || (() => {})
  });

  dialog.appendChild(createDialogHeader(title, close));

  const body = document.createElement('div');
  body.className = 'dialog-body';

  const section = document.createElement('div');
  section.className = 'dialog-section';
  section.style.padding = '10px 0';
  section.style.textAlign = 'center';

  const msgP = document.createElement('p');
  msgP.style.margin = '0';
  msgP.style.color = 'var(--text-primary)';
  msgP.style.fontSize = '14px';
  msgP.textContent = message;

  section.appendChild(msgP);
  body.appendChild(section);
  dialog.appendChild(body);

  const footer = createDialogFooter([
    {
      label: t('Cancel'),
      className: 'dialog-btn--secondary',
      onClick: () => {
        if (onCancel) onCancel();
        close();
      }
    },
    {
      label: t('Confirm'),
      className: 'dialog-btn--primary',
      onClick: () => {
        if (onConfirm) onConfirm();
        close();
      }
    }
  ]);

  dialog.appendChild(footer);
}

/**
 * Show a Save Confirmation dialog.
 *
 * @param {string} filename - The file being closed/switched.
 * @param {Function} onSave - Called when user clicks "Save".
 * @param {Function} onDiscard - Called when user clicks "Don't Save".
 * @param {Function} onCancel - Called when user cancels.
 */
export function showSaveConfirmDialog(filename, onSave, onDiscard, onCancel) {
  const { dialog, close } = createDialogBase({
    className: 'dialog-save-confirm',
    ariaLabel: t('Save Changes'),
    onClose: () => { if (typeof onCancel === 'function') onCancel(); },
  });

  dialog.appendChild(createDialogHeader(t('Save Changes'), close));

  const body = document.createElement('div');
  body.className = 'dialog-body';

  const icon = document.createElement('div');
  icon.className = 'dialog-save-icon';
  icon.innerHTML = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>`;

  const msg = document.createElement('p');
  msg.className = 'dialog-message';
  msg.textContent = `${t('Do you want to save changes to')} "${filename}"?`;

  const hint = document.createElement('p');
  hint.className = 'dialog-message-hint';
  hint.textContent = t('Your changes will be lost if you don\'t save them.');

  body.appendChild(icon);
  body.appendChild(msg);
  body.appendChild(hint);
  dialog.appendChild(body);

  const footer = createDialogFooter([
    {
      label: t('Save'),
      className: 'dialog-btn--primary',
      onClick: () => { close(); if (typeof onSave === 'function') onSave(); },
    },
    {
      label: t('Don\'t Save'),
      className: 'dialog-btn--danger',
      onClick: () => { close(); if (typeof onDiscard === 'function') onDiscard(); },
    },
    {
      label: t('Cancel'),
      className: 'dialog-btn--secondary',
      onClick: () => { close(); if (typeof onCancel === 'function') onCancel(); },
    },
  ]);
  
  // Center the buttons for this specific dialog
  footer.style.justifyContent = 'center';
  
  dialog.appendChild(footer);
}

/**
 * Show a WebDAV Connect dialog.
 *
 * @param {(config: {url: string, username: string, password: string, remember: boolean}) => void} onConnect
 */
export function showWebDAVConnectDialog(onConnect) {
  const { dialog, close } = createDialogBase({
    className: 'dialog-webdav',
    ariaLabel: t('Connect to WebDAV'),
    onClose: () => {},
  });

  dialog.appendChild(createDialogHeader(t('Connect to WebDAV'), close));

  const body = document.createElement('div');
  body.className = 'dialog-body';

  // URL
  const urlLabel = document.createElement('label');
  urlLabel.className = 'dialog-label';
  urlLabel.textContent = t('Server URL');
  urlLabel.htmlFor = 'webdav-url';

  const urlInput = document.createElement('input');
  urlInput.type = 'url';
  urlInput.id = 'webdav-url';
  urlInput.className = 'dialog-input';
  urlInput.placeholder = 'https://example.com/dav/';
  urlInput.setAttribute('aria-label', t('Server URL'));

  // Username
  const userLabel = document.createElement('label');
  userLabel.className = 'dialog-label';
  userLabel.textContent = t('Username');
  userLabel.htmlFor = 'webdav-user';

  const userInput = document.createElement('input');
  userInput.type = 'text';
  userInput.id = 'webdav-user';
  userInput.className = 'dialog-input';
  userInput.placeholder = t('Username');
  userInput.setAttribute('aria-label', t('Username'));
  userInput.autocomplete = 'username';

  // Password
  const passLabel = document.createElement('label');
  passLabel.className = 'dialog-label';
  passLabel.textContent = t('Password');
  passLabel.htmlFor = 'webdav-pass';

  const passInput = document.createElement('input');
  passInput.type = 'password';
  passInput.id = 'webdav-pass';
  passInput.className = 'dialog-input';
  passInput.placeholder = t('Password');
  passInput.setAttribute('aria-label', t('Password'));
  passInput.autocomplete = 'current-password';

  // Remember checkbox
  const rememberWrap = document.createElement('label');
  rememberWrap.className = 'dialog-checkbox-label';

  const rememberCheck = document.createElement('input');
  rememberCheck.type = 'checkbox';
  rememberCheck.id = 'webdav-remember';
  rememberCheck.className = 'dialog-checkbox';

  const rememberText = document.createElement('span');
  rememberText.textContent = t('Remember me');

  rememberWrap.appendChild(rememberCheck);
  rememberWrap.appendChild(rememberText);

  body.appendChild(urlLabel);
  body.appendChild(urlInput);
  body.appendChild(userLabel);
  body.appendChild(userInput);
  body.appendChild(passLabel);
  body.appendChild(passInput);
  body.appendChild(rememberWrap);
  dialog.appendChild(body);

  function handleConnect() {
    const url = urlInput.value.trim();
    if (!url) {
      urlInput.classList.add('dialog-input--error');
      urlInput.focus();
      return;
    }
    if (typeof onConnect === 'function') {
      onConnect({
        url,
        username: userInput.value,
        password: passInput.value,
        remember: rememberCheck.checked,
      });
    }
    close();
  }

  // Enter to submit from any input
  [urlInput, userInput, passInput].forEach((inp) => {
    inp.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConnect();
      }
    });
    inp.addEventListener('input', () => {
      inp.classList.remove('dialog-input--error');
    });
  });

  dialog.appendChild(createDialogFooter([
    { label: t('Cancel'), className: 'dialog-btn--secondary', onClick: close },
    { label: t('Connect'), className: 'dialog-btn--primary', onClick: handleConnect },
  ]));
}

/**
 * Show the Settings dialog.
 *
 * @param {object} settings - Current settings values.
 * @param {number} [settings.tabSize] - Tab size (2, 4, or 8).
 * @param {number} [settings.fontSize] - Font size in px.
 * @param {boolean} [settings.wordWrap] - Word wrap enabled.
 * @param {boolean} [settings.showInvisibles] - Show invisible characters.
 * @param {boolean} [settings.autoSave] - Auto-save enabled.
 * @param {(settings: object) => void} onSave - Called with updated settings.
 */
export function showSettingsDialog(settings = {}, onSave) {
  const current = {
    tabSize: settings.tabSize ?? 4,
    fontSize: settings.fontSize ?? 14,
    wordWrap: settings.wordWrap ?? false,
    showInvisibles: settings.showInvisibles ?? false,
    autoSave: settings.autoSave ?? false,
  };

  const { dialog, close } = createDialogBase({
    className: 'dialog-settings',
    ariaLabel: t('Settings'),
    onClose: () => {},
  });

  dialog.appendChild(createDialogHeader(t('Settings'), close));

  const body = document.createElement('div');
  body.className = 'dialog-body dialog-settings-body';

  // ── Tab Size ────────────────────────────────────────────────────────────
  const tabSizeRow = createSettingRow(t('Tab Size'));
  const tabSizeGroup = document.createElement('div');
  tabSizeGroup.className = 'dialog-btn-group';

  [2, 4, 8].forEach((size) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dialog-btn-group-item';
    if (current.tabSize === size) btn.classList.add('dialog-btn-group-item--active');
    btn.textContent = String(size);
    btn.addEventListener('click', () => {
      current.tabSize = size;
      tabSizeGroup.querySelectorAll('.dialog-btn-group-item').forEach((b) =>
        b.classList.remove('dialog-btn-group-item--active')
      );
      btn.classList.add('dialog-btn-group-item--active');
    });
    tabSizeGroup.appendChild(btn);
  });

  tabSizeRow.appendChild(tabSizeGroup);
  body.appendChild(tabSizeRow);

  // ── Font Size ───────────────────────────────────────────────────────────
  const fontSizeRow = createSettingRow(t('Font Size'));
  const fontSizeWrap = document.createElement('div');
  fontSizeWrap.className = 'dialog-slider-wrap';

  const fontSizeSlider = document.createElement('input');
  fontSizeSlider.type = 'range';
  fontSizeSlider.className = 'dialog-slider';
  fontSizeSlider.min = '10';
  fontSizeSlider.max = '32';
  fontSizeSlider.step = '1';
  fontSizeSlider.value = String(current.fontSize);
  fontSizeSlider.setAttribute('aria-label', 'Font size');

  const fontSizeValue = document.createElement('span');
  fontSizeValue.className = 'dialog-slider-value';
  fontSizeValue.textContent = `${current.fontSize}px`;

  fontSizeSlider.addEventListener('input', () => {
    current.fontSize = parseInt(fontSizeSlider.value, 10);
    fontSizeValue.textContent = `${current.fontSize}px`;
  });

  fontSizeWrap.appendChild(fontSizeSlider);
  fontSizeWrap.appendChild(fontSizeValue);
  fontSizeRow.appendChild(fontSizeWrap);
  body.appendChild(fontSizeRow);

  // ── Toggle settings ─────────────────────────────────────────────────────
  body.appendChild(createToggleRow(t('Word Wrap'), current.wordWrap, (val) => { current.wordWrap = val; }));
  body.appendChild(createToggleRow(t('Show Invisibles'), current.showInvisibles, (val) => { current.showInvisibles = val; }));
  body.appendChild(createToggleRow(t('Auto Save'), current.autoSave, (val) => { current.autoSave = val; }));

  dialog.appendChild(body);

  dialog.appendChild(createDialogFooter([
    { label: t('Cancel'), className: 'dialog-btn--secondary', onClick: close },
    {
      label: t('Save'),
      className: 'dialog-btn--primary',
      onClick: () => {
        if (typeof onSave === 'function') onSave({ ...current });
        close();
      },
    },
  ]));
}

// ─── Setting row helpers ─────────────────────────────────────────────────────

/**
 * Create a settings row with label.
 * @param {string} labelText
 * @returns {HTMLElement}
 */
function createSettingRow(labelText) {
  const row = document.createElement('div');
  row.className = 'dialog-setting-row';

  const label = document.createElement('label');
  label.className = 'dialog-setting-label';
  label.textContent = labelText;
  row.appendChild(label);

  return row;
}

/**
 * Create a toggle switch row.
 * @param {string} labelText
 * @param {boolean} initialValue
 * @param {(value: boolean) => void} onChange
 * @returns {HTMLElement}
 */
function createToggleRow(labelText, initialValue, onChange) {
  const row = document.createElement('div');
  row.className = 'dialog-setting-row';

  const label = document.createElement('label');
  label.className = 'dialog-setting-label';
  label.textContent = labelText;

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'dialog-toggle';
  toggle.setAttribute('role', 'switch');
  toggle.setAttribute('aria-checked', String(initialValue));
  toggle.setAttribute('aria-label', labelText);
  if (initialValue) toggle.classList.add('dialog-toggle--on');

  const knob = document.createElement('span');
  knob.className = 'dialog-toggle-knob';
  toggle.appendChild(knob);

  toggle.addEventListener('click', () => {
    const isOn = toggle.getAttribute('aria-checked') === 'true';
    toggle.setAttribute('aria-checked', String(!isOn));
    toggle.classList.toggle('dialog-toggle--on', !isOn);
    if (typeof onChange === 'function') onChange(!isOn);
  });

  row.appendChild(label);
  row.appendChild(toggle);

  return row;
}

export function showHelpDialog() {
  const { overlay, dialog, close } = createDialogBase({
    className: 'settings-dialog',
    ariaLabel: t('Help'),
  });

  const header = document.createElement('div');
  header.className = 'dialog-header';
  const title = document.createElement('h2');
  title.className = 'dialog-title';
  title.textContent = t('MyPad++ Help');
  header.appendChild(title);

  const body = document.createElement('div');
  body.className = 'dialog-body';
  body.style.maxHeight = '60vh';
  body.style.overflowY = 'auto';
  body.style.lineHeight = '1.6';

  body.innerHTML = t('HELP_CONTENT_HTML', {
    newFile: ICONS.newFile,
    open: ICONS.open,
    save: ICONS.save,
    saveAs: ICONS.saveAs,
    undo: ICONS.undo,
    redo: ICONS.redo,
    wordWrap: ICONS.wordWrap,
    statusBar: ICONS.statusBar,
    keyboardOff: ICONS.keyboardOff,
    fullscreen: ICONS.fullscreen,
    zoomIn: ICONS.zoomIn,
    zoomOut: ICONS.zoomOut,
    themeDark: ICONS.themeDark,
    themeLight: ICONS.themeLight,
    explorer: ICONS.explorer,
    find: ICONS.find,
    replace: ICONS.replace,
    nextError: ICONS.nextError,
    compare: ICONS.compare,
    webdav: ICONS.webdav,
    workspace: ICONS.workspace,
    highlights: ICONS.highlights,
  });

  const footer = document.createElement('div');
  footer.className = 'dialog-footer';
  
  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn btn-ghost';
  closeBtn.textContent = t('Close');
  closeBtn.addEventListener('click', close);
  
  footer.appendChild(closeBtn);

  dialog.appendChild(header);
  dialog.appendChild(body);
  dialog.appendChild(footer);

  document.body.appendChild(overlay);
  closeBtn.focus();
}

let loadingOverlay = null;

export function showLoading(msg = 'Loading...') {
  hideLoading();
  loadingOverlay = document.createElement('div');
  loadingOverlay.style.position = 'fixed';
  loadingOverlay.style.top = '0';
  loadingOverlay.style.left = '0';
  loadingOverlay.style.width = '100vw';
  loadingOverlay.style.height = '100vh';
  loadingOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.4)';
  loadingOverlay.style.zIndex = '9999';
  loadingOverlay.style.display = 'flex';
  loadingOverlay.style.flexDirection = 'column';
  loadingOverlay.style.alignItems = 'center';
  loadingOverlay.style.justifyContent = 'center';
  loadingOverlay.style.backdropFilter = 'blur(2px)';
  
  const spinner = document.createElement('div');
  spinner.style.width = '40px';
  spinner.style.height = '40px';
  spinner.style.border = '4px solid rgba(255,255,255,0.3)';
  spinner.style.borderTop = '4px solid white';
  spinner.style.borderRadius = '50%';
  spinner.style.animation = 'mypad-spin 1s linear infinite';
  
  const text = document.createElement('div');
  text.style.color = 'white';
  text.style.marginTop = '16px';
  text.style.fontSize = '14px';
  text.style.fontWeight = '500';
  text.style.fontFamily = 'var(--font-ui)';
  text.textContent = msg;

  if (!document.getElementById('mypad-spin-style')) {
    const style = document.createElement('style');
    style.id = 'mypad-spin-style';
    style.textContent = '@keyframes mypad-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }

  loadingOverlay.appendChild(spinner);
  loadingOverlay.appendChild(text);
  document.body.appendChild(loadingOverlay);
}

export async function showWorkspaceSaveAsDialog(client, initialDir, defaultFilename, onSave, onSaveLocal) {
  let currentDir = initialDir || '/';

  const { overlay, dialog, close } = createDialogBase({
    className: 'workspace-save-dialog',
    ariaLabel: t('Save to Server'),
  });
  dialog.style.width = '480px';
  dialog.style.maxWidth = '90vw';

  const header = document.createElement('div');
  header.className = 'dialog-header';
  const title = document.createElement('h2');
  title.className = 'dialog-title';
  title.textContent = t('Save to Server');
  header.appendChild(title);

  const pathLabel = document.createElement('div');
  pathLabel.style.padding = '0 24px';
  pathLabel.style.fontSize = '12px';
  pathLabel.style.color = 'var(--text-secondary)';
  pathLabel.style.whiteSpace = 'nowrap';
  pathLabel.style.overflow = 'hidden';
  pathLabel.style.textOverflow = 'ellipsis';
  pathLabel.textContent = t('Current Directory:') + ' ' + currentDir;

  const listContainer = document.createElement('div');
  listContainer.style.height = '250px';
  listContainer.style.overflowY = 'auto';
  listContainer.style.border = '1px solid var(--bg-modifier-hover)';
  listContainer.style.margin = '8px 24px';
  listContainer.style.borderRadius = '4px';
  listContainer.style.display = 'grid';
  listContainer.style.gridTemplateColumns = 'repeat(auto-fill, minmax(80px, 1fr))';
  listContainer.style.gridAutoRows = 'min-content';
  listContainer.style.gap = '8px';
  listContainer.style.padding = '12px';
  listContainer.style.alignContent = 'start';

  async function renderList() {
    listContainer.innerHTML = '<div style="grid-column: 1 / -1; padding: 12px; text-align: center;">Loading...</div>';
    pathLabel.textContent = t('Current Directory:') + ' ' + currentDir;
    try {
      const items = await client.listDirectory(currentDir);
      listContainer.innerHTML = '';
      
      const createFolderItem = (name, path) => {
        const div = document.createElement('div');
        div.style.display = 'flex';
        div.style.flexDirection = 'column';
        div.style.alignItems = 'center';
        div.style.justifyContent = 'center';
        div.style.padding = '8px';
        div.style.cursor = 'pointer';
        div.style.borderRadius = '4px';
        div.style.transition = 'background-color 0.15s';
        
        div.onmouseenter = () => div.style.backgroundColor = 'var(--bg-modifier-hover)';
        div.onmouseleave = () => div.style.backgroundColor = 'transparent';
        
        div.onclick = () => {
          currentDir = path;
          renderList();
        };

        const icon = document.createElement('div');
        icon.style.fontSize = '36px';
        icon.style.marginBottom = '4px';
        icon.textContent = '📁';
        
        const label = document.createElement('div');
        label.style.fontSize = '12px';
        label.style.width = '100%';
        label.style.textAlign = 'center';
        label.style.overflow = 'hidden';
        label.style.textOverflow = 'ellipsis';
        label.style.whiteSpace = 'nowrap';
        label.textContent = name;
        label.title = name; // tooltip for full name
        
        div.appendChild(icon);
        div.appendChild(label);
        return div;
      };

      if (currentDir !== '/') {
        listContainer.appendChild(createFolderItem('..', currentDir.replace(/\/[^/]+$/, '') || '/'));
      }

      items.filter(i => i.isDirectory).forEach(item => {
        listContainer.appendChild(createFolderItem(item.name, item.path));
      });
    } catch (e) {
      listContainer.innerHTML = '<div style="grid-column: 1 / -1; padding: 12px; color: red;">' + e.message + '</div>';
    }
  }

  const inputContainer = document.createElement('div');
  inputContainer.className = 'dialog-body';
  inputContainer.style.paddingTop = '8px';
  
  const label = document.createElement('label');
  label.textContent = t('File Name:');
  label.style.display = 'block';
  label.style.marginBottom = '8px';
  
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'dialog-input';
  input.value = defaultFilename || '';
  
  inputContainer.appendChild(label);
  inputContainer.appendChild(input);

  const footer = document.createElement('div');
  footer.className = 'dialog-footer';
  
  const localBtn = document.createElement('button');
  localBtn.className = 'btn btn-ghost';
  localBtn.textContent = t('Local Machine...');
  localBtn.onclick = () => {
    close();
    if (onSaveLocal) onSaveLocal();
  };

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn btn-ghost';
  cancelBtn.textContent = t('Cancel');
  cancelBtn.onclick = close;

  const saveBtn = document.createElement('button');
  saveBtn.className = 'btn btn-primary';
  saveBtn.textContent = t('Save');
  saveBtn.onclick = () => {
    if (!input.value.trim()) return;
    const fullPath = (currentDir.endsWith('/') ? currentDir : currentDir + '/') + input.value.trim();
    close();
    onSave(fullPath);
  };

  footer.appendChild(localBtn);
  footer.style.justifyContent = 'space-between';
  
  const rightBtns = document.createElement('div');
  rightBtns.style.display = 'flex';
  rightBtns.style.gap = '8px';
  rightBtns.appendChild(cancelBtn);
  rightBtns.appendChild(saveBtn);
  
  footer.appendChild(rightBtns);

  dialog.appendChild(header);
  dialog.appendChild(pathLabel);
  dialog.appendChild(listContainer);
  dialog.appendChild(inputContainer);
  dialog.appendChild(footer);

  document.body.appendChild(overlay);
  input.focus();
  input.select();
  
  renderList();
}

export function hideLoading() {
  if (loadingOverlay && loadingOverlay.parentNode) {
    loadingOverlay.parentNode.removeChild(loadingOverlay);
    loadingOverlay = null;
  }
}

export function updateLoadingMessage(msg) {
  if (loadingOverlay) {
    const textEl = loadingOverlay.querySelector('div:last-child');
    if (textEl) {
      textEl.textContent = msg;
    }
  }
}

export function showFileChangedDialog(filename, onReload, onCompare, onIgnore) {
  const { overlay, dialog, close } = createDialogBase({
    className: 'file-changed-dialog',
    ariaLabel: t('File Changed on Server'),
  });

  const header = document.createElement('div');
  header.className = 'dialog-header';
  const title = document.createElement('h2');
  title.className = 'dialog-title';
  title.style.color = 'var(--text-error)';
  title.textContent = t('File Changed on Server');
  header.appendChild(title);

  const body = document.createElement('div');
  body.className = 'dialog-body';
  const p = document.createElement('p');
  p.textContent = t('The file') + ' "' + filename + '" ' + t('has been modified on the server by another client.');
  p.style.marginBottom = '12px';
  const p2 = document.createElement('p');
  p2.textContent = t('What would you like to do?');
  
  body.appendChild(p);
  body.appendChild(p2);

  const footer = document.createElement('div');
  footer.className = 'dialog-footer';
  footer.style.justifyContent = 'flex-end';
  footer.style.gap = '8px';
  
  const ignoreBtn = document.createElement('button');
  ignoreBtn.className = 'btn btn-ghost';
  ignoreBtn.textContent = t('Ignore');
  ignoreBtn.onclick = () => {
    close();
    if (onIgnore) onIgnore();
  };

  const compareBtn = document.createElement('button');
  compareBtn.className = 'btn btn-ghost';
  compareBtn.textContent = t('Compare');
  compareBtn.onclick = () => {
    close();
    if (onCompare) onCompare();
  };

  const reloadBtn = document.createElement('button');
  reloadBtn.className = 'btn btn-primary';
  reloadBtn.textContent = t('Reload');
  reloadBtn.onclick = () => {
    close();
    if (onReload) onReload();
  };

  footer.appendChild(ignoreBtn);
  footer.appendChild(compareBtn);
  footer.appendChild(reloadBtn);

  dialog.appendChild(header);
  dialog.appendChild(body);
  dialog.appendChild(footer);

  document.body.appendChild(overlay);
}
